import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import FiltersLabel from '../../components/FiltersLabel';
import {
    fetchCronjobNotifications,
    fetchCronjobNotificationById,
    clearSelectedNotification
} from '../../slices/cronjob-notifications.slice';
import { fetchOrganizations } from '../../slices/organizations.slice';
import Loading from '../../components/Loading';
import { DatePicker } from '../../components/DateTime';
import {
    Badge,
    Button,
    ButtonGroup,
    Label,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';
import { getFiterDatesLabel } from '../../utils/dates';
import SearchBar from '../../components/SearchBar';
import OrganizationPatch from '../../components/OrganizationPatch';
import { imgUrl } from '../../utils/api-request';
import { ORDER_ACTION_CONFIG, EVENT_TYPE_CONFIG, getOrderActionConfig, getEventTypeConfig } from './constants';
import CronjobNotificationDetailPanel from './CronjobNotificationDetailPanel';
import './CronjobNotifications.scss';

const CronjobNotifications = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { notificationId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { list: notifications, status, pagination } = useSelector(state => state.cronjobNotifications);
    const { list: organizations } = useSelector(state => state.organizations);
    const [searchValue, setSearchInput] = useState(searchParams.get('search') || '');
    const [searchModalValue, setSearchModalValue] = useState('');
    const debounceRef = useRef(null);
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '', filterName: '' });

    const isPanelOpen = !!notificationId;

    const selectNotification = (id) => {
        navigate(`/cronjob-notifications/${id}${window.location.search}`);
    };

    const closePanel = () => {
        dispatch(clearSelectedNotification());
        navigate(`/cronjob-notifications${window.location.search}`);
    };

    useEffect(() => {
        if (notificationId) {
            dispatch(fetchCronjobNotificationById(notificationId));
        }
    }, [dispatch, notificationId]);

    const parseURLFilters = () => {
        const organizationsParam = searchParams.get('organizations');
        const eventTypesParam = searchParams.get('eventTypes');
        const orderActionsParam = searchParams.get('orderActions');
        const dateFromParam = searchParams.get('dateFrom');
        const dateToParam = searchParams.get('dateTo');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');

        return {
            organizations: organizationsParam ? organizationsParam.split(',') : [],
            eventTypes: eventTypesParam ? eventTypesParam.split(',') : [],
            orderActions: orderActionsParam ? orderActionsParam.split(',') : [],
            createdAt: {
                from: dateFromParam ? new Date(dateFromParam) : null,
                to: dateToParam ? new Date(dateToParam) : null
            },
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());

    const toggleFilterModal = (filter, filterName) => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter, filterName });
    };

    const formatDateForURL = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.organizations.length > 0) {
            params.set('organizations', newFilters.organizations.join(','));
        }
        if (newFilters.eventTypes.length > 0) {
            params.set('eventTypes', newFilters.eventTypes.join(','));
        }
        if (newFilters.orderActions.length > 0) {
            params.set('orderActions', newFilters.orderActions.join(','));
        }
        if (newFilters.createdAt.from) {
            params.set('dateFrom', formatDateForURL(newFilters.createdAt.from));
        }
        if (newFilters.createdAt.to) {
            params.set('dateTo', formatDateForURL(newFilters.createdAt.to));
        }
        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.page && newFilters.page !== 1) {
            params.set('page', newFilters.page);
        }

        setSearchParams(params);
    };

    const handleSearch = (event) => {
        event.preventDefault();
        const query = event.target.value;
        setSearchInput(query);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setFilterBy(prev => {
                const newFilters = { ...prev, search: query, page: 1 };
                updateURLParams(newFilters);
                return newFilters;
            });
        }, 400);
    };

    const toggleFilter = (filterKey, value) => {
        setFilterBy((prevFilterBy) => {
            const isSelected = prevFilterBy[filterKey].includes(value);
            const newFilters = {
                ...prevFilterBy,
                [filterKey]: isSelected
                    ? prevFilterBy[filterKey].filter((item) => item !== value)
                    : [...prevFilterBy[filterKey], value],
                page: 1
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const handleCreatedAt = (period) => (date) => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                createdAt: {
                    ...prevFilterBy.createdAt,
                    [period]: date
                },
                page: 1
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const areFiltersActive = useMemo(() => {
        const { organizations: orgs, eventTypes, orderActions, createdAt } = filterBy;

        return (
            orgs.length > 0 ||
            eventTypes.length > 0 ||
            orderActions.length > 0 ||
            createdAt.from !== null ||
            createdAt.to !== null
        );
    }, [filterBy]);

    const clearFilters = () => {
        const newFilters = {
            organizations: [],
            eventTypes: [],
            orderActions: [],
            createdAt: { from: null, to: null },
            search: '',
            page: 1
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handlePageChange = (newPage) => {
        const newFilters = {
            ...filterBy,
            page: newPage
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    useEffect(() => {
        const params = {
            organizations: filterBy.organizations,
            eventTypes: filterBy.eventTypes,
            orderActions: filterBy.orderActions,
            page: filterBy.page
        };

        if (filterBy.search) {
            params.search = filterBy.search;
        }

        if (filterBy.createdAt.from) {
            const fromDate = new Date(filterBy.createdAt.from);
            fromDate.setHours(0, 0, 0, 0);
            params.dateFrom = fromDate.toISOString();
        }

        if (filterBy.createdAt.to) {
            const toDate = new Date(filterBy.createdAt.to);
            toDate.setHours(23, 59, 59, 999);
            params.dateTo = toDate.toISOString();
        }

        dispatch(fetchCronjobNotifications(params));
    }, [dispatch, filterBy]);

    useEffect(() => {
        dispatch(fetchOrganizations());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="cronjob-notification-view page px-4 py-3 d-flex flex-column overflow-y-auto">
            <PageHeader
                title="Cron Notifications"
                searchBar={{ searchValue, handleSearch }}
            />

            <div className="d-flex justify-content-start align-items-center gap-3 flex-wrap">
                <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                <ButtonGroup className="gap-2">
                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('organizations', 'Organizations')}>
                        {filterBy.organizations.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.organizations.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-building me-2" />
                        )}
                        Organizations
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('eventTypes', 'Event Types')}>
                        {filterBy.eventTypes.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.eventTypes.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-code-branch me-2" />
                        )}
                        Event Types
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('orderActions', 'Order Actions')}>
                        {filterBy.orderActions.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.orderActions.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-clipboard-list me-2" />
                        )}
                        Order Actions
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('createdAt', 'dates')}>
                        {filterBy.createdAt.from || filterBy.createdAt.to ? (
                            <Badge color="success" className="me-2 text-white">
                                <span className="fas fa-calendar-check" />
                            </Badge>
                        ) : (
                            <span className="fas fa-calendar-week me-2" />
                        )}
                        {getFiterDatesLabel(filterBy.createdAt, 'Dates')}
                    </Button>
                </ButtonGroup>

                <div className="flex-grow-1">
                    <span className="fas fa-list me-2 text-primary" />
                    showing {pagination.totalCount} result{pagination.totalCount === 1 ? '' : 's'}
                </div>
            </div>

            {status === 'loading' && <Loading length={3} />}

            {status === 'success' && (
                <div className="cronjob-notification-list">
                    {notifications.map((notification) => {
                        const actionConfig = getOrderActionConfig(notification.orderAction);
                        const eventConfig = getEventTypeConfig(notification.eventType);
                        const movieversion = notification.movieversionId?.name ? notification.movieversionId : null;
                        const movie = movieversion?.movie?.title ? movieversion.movie : null;
                        const organization = notification.organization?.name
                            ? notification.organization
                            : (notification.organizationName ? { name: notification.organizationName } : null);

                        return (
                            <div
                                key={notification._id}
                                className={`cronjob-notification-item my-2 p-3 bg-broken border-start d-flex justify-content-between action-${actionConfig.color} ${notificationId === notification._id ? 'selected' : ''}`}
                                onClick={() => selectNotification(notification._id)}
                            >
                                <div className="action-icon">
                                    <span className={`fas ${actionConfig.icon}`} />
                                </div>

                                <div className="poster-thumb flex-shrink-0 me-3">
                                    {movie?.poster?.imageUrl ? (
                                        <img src={imgUrl(movie.poster.imageUrl)} alt={movie.title} />
                                    ) : (
                                        <div className="poster-thumb-placeholder d-flex align-items-center justify-content-center">
                                            <span className="fas fa-film text-white-50" />
                                        </div>
                                    )}
                                </div>

                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                        <Badge color={actionConfig.color} style={{ borderRadius: 0 }}>
                                            <span className={`fas ${actionConfig.icon} me-1`} />
                                            {actionConfig.label}
                                        </Badge>
                                        <Badge color={eventConfig.color} style={{ borderRadius: 0 }}>
                                            <span className={`fas ${eventConfig.icon} me-1`} />
                                            {eventConfig.label}
                                        </Badge>
                                    </div>
                                    <p className="mb-1 text-truncate">
                                        <span className="fas fa-film me-2 text-primary" />
                                        {movie?.title || notification.payload?.movieversionName || 'Unknown movie'}
                                    </p>
                                    <p className="mb-0 small text-white-50 text-truncate">
                                        <span className="fas fa-clapperboard me-1" />
                                        {movieversion?.name || notification.payload?.movieversionName || 'Unknown movieversion'}
                                        {notification.movieversionUuid && (
                                            <code className="ms-2 text-white-50">{notification.movieversionUuid}</code>
                                        )}
                                    </p>
                                </div>

                                <div className="d-flex flex-column align-items-end gap-2 ms-3 flex-shrink-0">
                                    <div className="small text-white-50">
                                        <span className="fas fa-calendar me-2" />
                                        {new Date(notification.createdAt).toLocaleString()}
                                    </div>
                                    <OrganizationPatch organization={organization} />
                                </div>
                            </div>
                        );
                    })}

                    {notifications.length === 0 && (
                        <div className="d-flex flex-column align-items-center py-5">
                            <span className="fas fa-circle-check fa-3x text-success mb-3 mt-5 pt-5" />
                            {areFiltersActive || filterBy.search ? (
                                <>
                                    <p className="text-white-50 mb-3">No cronjob notifications match your filters</p>
                                    <Button color="primary" outline onClick={clearFilters}>
                                        Clear filters
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <h5 className="text-white mb-2">Nothing Recorded</h5>
                                    <p className="text-white-50">No cronjob notifications recorded yet</p>
                                </>
                            )}
                        </div>
                    )}

                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center gap-3 my-4">
                            <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={pagination.currentPage === 1}
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                            >
                                <span className="fas fa-chevron-left me-2" />
                                Previous
                            </Button>

                            <span className="text-white">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </span>

                            <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={!pagination.hasMore}
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                            >
                                Next
                                <span className="fas fa-chevron-right ms-2" />
                            </Button>
                        </div>
                    )}

                    {/* Order Action Legend */}
                    <div className="cronjob-action-legend">
                        <div className="legend-item legend-success">
                            <div className="legend-dot"><span className="fas fa-circle-plus" /></div>
                            Order created / updated
                        </div>
                        <div className="legend-item legend-info">
                            <div className="legend-dot"><span className="fas fa-cloud-arrow-down" /></div>
                            Synced from cloud
                        </div>
                        <div className="legend-item legend-warning">
                            <div className="legend-dot"><span className="fas fa-circle-minus" /></div>
                            No order / deprecated
                        </div>
                        <div className="legend-item legend-danger">
                            <div className="legend-dot"><span className="fas fa-triangle-exclamation" /></div>
                            Organization mismatch
                        </div>
                        <div className="legend-item legend-secondary">
                            <div className="legend-dot"><span className="fas fa-forward" /></div>
                            Skipped
                        </div>
                    </div>
                </div>
            )}

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="lg" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    Filter by {filterModal.filterName || filterModal.filter}
                </ModalHeader>
                <ModalBody>
                    {filterModal.filter === 'organizations' && (
                        <div className="organizations-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchModalValue(target.value)} initialValue={searchModalValue} />
                            <ListGroup>
                                {organizations.map(({ _id, name, logo }) => name.toLowerCase().includes(searchModalValue.toLowerCase()) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('organizations', _id)}
                                        key={_id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.organizations.includes(_id)}
                                        className="border-0 border-start border-primary border-2 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <img src={imgUrl(logo?.imageUrl)} width={50} alt="" />
                                        <div className="flex-grow-1">
                                            {name}
                                        </div>
                                        {filterBy.organizations.includes(_id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'eventTypes' && (
                        <div className="event-types-filter-modal">
                            <ListGroup>
                                {Object.entries(EVENT_TYPE_CONFIG).map(([type, config]) => (
                                    <ListGroupItem
                                        key={type}
                                        onClick={() => toggleFilter('eventTypes', type)}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.eventTypes.includes(type)}
                                        className={`border-0 border-start border-${config.color} border-4 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2`}
                                    >
                                        <div
                                            className={`rounded-circle bg-dark d-flex align-items-center justify-content-center text-${config.color}`}
                                            style={{ width: '32px', height: '32px', border: '2px solid currentColor' }}
                                        >
                                            <span className={`fas ${config.icon} fa-sm`} />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="fw-medium">{config.label}</span>
                                            <p className="mb-0 small text-white-50">{config.description}</p>
                                        </div>
                                        {filterBy.eventTypes.includes(type) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'orderActions' && (
                        <div className="order-actions-filter-modal">
                            <ListGroup>
                                {Object.entries(ORDER_ACTION_CONFIG).map(([action, config]) => (
                                    <ListGroupItem
                                        key={action}
                                        onClick={() => toggleFilter('orderActions', action)}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.orderActions.includes(action)}
                                        className={`border-0 border-start border-${config.color} border-4 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2`}
                                    >
                                        <div
                                            className={`rounded-circle bg-dark d-flex align-items-center justify-content-center text-${config.color}`}
                                            style={{ width: '32px', height: '32px', border: '2px solid currentColor' }}
                                        >
                                            <span className={`fas ${config.icon} fa-sm`} />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="fw-medium">{config.label}</span>
                                            <p className="mb-0 small text-white-50">{config.description}</p>
                                        </div>
                                        {filterBy.orderActions.includes(action) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'createdAt' && (
                        <div className="createdAt-filter-modal">
                            <Label>Date from</Label>
                            <DatePicker
                                value={filterBy.createdAt.from}
                                onChange={handleCreatedAt('from')}
                                placeholder="From"
                                clearable
                            />
                            <Label className="mt-4">Date to</Label>
                            <DatePicker
                                value={filterBy.createdAt.to}
                                onChange={handleCreatedAt('to')}
                                placeholder="To"
                                clearable
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" />
                        Show results
                    </Button>
                </ModalFooter>
            </Modal>

            {isPanelOpen && (
                <CronjobNotificationDetailPanel onClose={closePanel} />
            )}
        </div>
    );
};

export default CronjobNotifications;
